import { getIn } from 'formik';

export const maxLength = length => (label, validator) =>
  validator.max(length, `${label} cannot exceed ${length} characters`);

export const minLength = length => (label, validator) =>
  validator.min(length, `${label} must be at least ${length} characters`);

export const required = (label, validator) =>
  validator.required(`${label} is required`);

export const requiredWithFlagInContext = flag => (label, validator, validationContext) => {
  const flags = Array.isArray(flag) ? flag : [flag];

  return flags.every(flag => validationContext[flag] === true)
    ? validator.required(`${label} is required.`)
    : validator.nullable();
};

export const yesNoFlag = (label, validator) =>
  validator.matches(/^[YN]$/, `${label} must be Y or N`);

export const yesNoAlwaysFlag = (label, validator) =>
  validator.matches(/^[YNA]$/, `${label} must be Y, N or A`);

export const nullable = (label, validator) =>
  validator.nullable();

export const integer = (label, validator) =>
  validator.integer(`${label} must be an integer`);

export const alphaNumeric = (label, validator) =>
  validator.matches(/^[a-zA-Z0-9]*$/, `${label} must be alphanumeric`);

export const underscoredAlphaNumeric = (label, validator) =>
  validator.matches(/^[a-zA-Z0-9_]*$/, `${label} must be alphanumeric with underscore`);

export const countryTaxTypeValidator = (label, validator) =>
  validator.matches(/^[A-Z]{2}[1-9]{1}$/, `${label} must begin with two character Country Code and end with one digit between 1-9`);

export const differentThan = (fieldName, label1, overrideFlag) => (label2, validator, validationContext) =>
  validationContext[overrideFlag]
    ? validator
    : validator.test('differentThan', `${label1} and ${label2} must be different`, function(value) {
      return !value || value !== this.parent[fieldName]; // eslint-disable-line babel/no-invalid-this
    });

export const uniqueWith = (fieldNames, label) => (_, validator, validationContext) =>
  validator.test('uniqueWith', label, function(value) {
    const { path, parent } = this; // eslint-disable-line babel/no-invalid-this
    const duplicates = validationContext.entities.filter(entry =>
      getIn(entry, path) === value
      && fieldNames.reduce((result, field) => result && getIn(entry, field) === getIn(parent, field), true)
    );

    if ((validationContext.openForEdit && duplicates.length <= 1) || duplicates.length === 0) {
      return true;
    }

    return false;
  });

export const uniq = (idFields, label) => (_, validator, validationContext) =>
  validator.test('uniq', label, function(value) {
    const { path, parent } = this; // eslint-disable-line babel/no-invalid-this

    const duplicates = idFields.length
      ? validationContext.entities.filter(entry =>
        getIn(entry, path) === value
        && !idFields.reduce((result, field) => result && getIn(entry, field) === getIn(parent, field), true)
      )
      : validationContext.entities.filter(entry => getIn(entry, path) === value);

    return validationContext.openForEdit && !idFields.length
      ? duplicates.length <= 1
      : duplicates.length === 0;
  });

export const oneOfExisting = (optionsKey, { nullable = true } = {}) => (_, validator, validationContext) =>
  // eslint-disable-next-line  no-template-curly-in-string
  validator.test('oneOfExisting', 'Selected ${value} option does not exist', function(selectedValue) {
    if (!selectedValue) {
      return nullable;
    }
    return validationContext[optionsKey].some(({ value }) => value === selectedValue);
  });

export const oneOfExistingTypeAndActivityCombination = (_, validator, validationContext) =>
  validator.test('oneOfExistingTypeAndActivityCombination', 'Type and Activity combination not valid', function() {
    const { parent } = this; // eslint-disable-line babel/no-invalid-this
    const foundEntity = validationContext.slTaxEntries.find(({
      type,
      activity,
    }) => parent.type === type
        && parent.activity === activity
    );
    return Boolean(foundEntity);
  });

export const arrayMinLength = (length, errorMessage) => (_, validator) => validator.min(length, errorMessage);

export const notAllowed = (notAllowedArray, errorMessage) => (_, validator) =>
  validator.test('notAllowed', errorMessage, function(value) {
    return !notAllowedArray.includes(value);
  });

export const permittedWhen = (condition, error) => (_, validator) =>
  validator.test('permittedWhen', error, function(value) {
    const { parent } = this; // eslint-disable-line babel/no-invalid-this
    if (!condition(parent) && value) {
      return false;
    }
    return true;
  });

export const validateValueIsUnique = ({ optionsKey, message, conditionFn }) =>
  function(label, validator, validationContext) {
    const options = validationContext[optionsKey];
    if (!options) {
      return validator;
    }
    return validator.test(
      'uniqueValidation',
      message,
      function(selectedValue, context) {
        if (!selectedValue || validationContext.openForEdit) {
          return true;
        }
        const exists = options.some(option => conditionFn(option, selectedValue, context));
        return !exists;
      }
    );
  };

